import { useQueryClient } from '@tanstack/react-query';
import { getToken, onMessage } from 'firebase/messaging';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { getFirebaseMessaging } from '../../firebase';
import { registerDevice, markNotificationRead } from '../../services/notificationService';
import { useAuthStore } from '../../stores/useAuthStore';
import { useNotificationStore } from '../../stores/useNotificationStore';
import { resolveNotificationRoute } from '../notificationActionResolver';
import { NOTIFICATION_QUERY_KEY } from './useNotifications';

const useFirebaseNotifications = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const fcmToken = useNotificationStore((state) => state.fcmToken);
  const setFcmToken = useNotificationStore((state) => state.setFcmToken);
  const addNotification = useNotificationStore(
    (state) => state.addNotification
  );
  const markAsRead = useNotificationStore((state) => state.markAsRead);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated) return;

    let unsubscribeForeground = () => {};

    const setup = async () => {
      // ── Step 1: Request browser notification permission ──────────────────
      // This is a plain browser API — works regardless of FCM support.
      // Must be called before any FCM-specific code so the prompt always shows.
      if (!('Notification' in window)) {
        console.warn('[FCM] Browser does not support notifications.');
        return;
      }

      // If already decided, don't prompt again — just check the state.
      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission !== 'granted') {
        console.warn('[FCM] Notification permission:', permission);
        return;
      }

      // ── Step 2: Check FCM support & initialise messaging lazily ──────────
      // Separated so a failed support check doesn't skip the permission prompt.
      try {
        if (!('serviceWorker' in navigator)) {
          console.warn('[FCM] Service workers not supported.');
          return;
        }

        const messaging = await getFirebaseMessaging();
        if (!messaging) {
          console.warn(
            '[FCM] Firebase Messaging not supported in this browser.'
          );
          return;
        }

        // ── Step 3: Get/refresh FCM token ───────────────────────────────────
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        });


        if (!token) {
          console.warn(
            '[FCM] Unable to generate device token. Check VAPID key and service worker registration.'
          );
          return;
        }

        // Only register with backend when token is new or has rotated
        if (token !== fcmToken) {
          await registerDevice(token);
          setFcmToken(token);
        }

        // ── Step 4: Foreground message listener ─────────────────────────────
        unsubscribeForeground = onMessage(messaging, (payload) => {
          const { title, body } = payload.notification || {};
          const data = payload.data || {};

          // Parse params safely
          let parsedParams = {};
          if (data.params) {
            try {
              parsedParams = JSON.parse(data.params);
            } catch {
              parsedParams = {};
            }
          }

          // Build a normalised notification matching the store shape
          const normalisedNotification = {
            id: data.id ? Number(data.id) : Date.now(),
            title: title || 'New Notification',
            message: body || '',
            isRead: false,
            createdAt: new Date().toISOString(),
            data: {
              action_type: data.action_type || null,
              params: parsedParams,
              id: data.id || null,
            },
          };

          addNotification(normalisedNotification);

          queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });

          const route = resolveNotificationRoute(
            data.action_type,
            parsedParams
          );

          toast(
            (t) => (
              <div className="flex items-start justify-between w-full relative group">
                <div
                  className="flex flex-col gap-1 cursor-pointer flex-1 pr-6"
                  onClick={() => {
                    toast.dismiss(t.id);
                    if (normalisedNotification.id && !normalisedNotification.isRead) {
                      markAsRead(normalisedNotification.id);
                      markNotificationRead(normalisedNotification.id).catch(
                        console.error
                      );
                      normalisedNotification.isRead = true;
                    }
                    if (route) window.location.href = route;
                  }}
                >
                  <span className="font-bold text-sm">
                    {title || 'New Notification'}
                  </span>
                  <span className="text-xs text-gray-600">{body}</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toast.dismiss(t.id);
                  }}
                  className="absolute top-0 right-0 p-1 text-gray-400 hover:text-gray-700 transition-colors opacity-0 group-hover:opacity-100 rounded-full hover:bg-gray-100"
                  title="Dismiss notification"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            ),
            { duration: 6000, position: 'top-right', icon: '🔔' }
          );
        });
      } catch (err) {
        console.error('[FCM] Setup error:', err);
      }
    };

    setup();

    // Re-fetch when the user's tab regains focus (cross-tab sync)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribeForeground();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useFirebaseNotifications;
